'use client'

import { useEffect, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { pubFetch } from '@/lib/client-fetch'

const PUBLIC_PATHS = new Set(['/login', '/register', '/docs'])

export function SessionGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const router = useRouter()
  const [ready, setReady] = useState(() => PUBLIC_PATHS.has(pathname ?? ''))

  useEffect(() => {
    const path = pathname ?? ''
    if (PUBLIC_PATHS.has(path)) {
      setReady(true)
      return
    }

    let cancelled = false

    async function validate() {
      try {
        let res = await pubFetch('/api/auth/session')

        if (res.status === 401) {
          const token = localStorage.getItem('token')
          if (token) {
            const sync = await pubFetch('/api/auth/sync-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token }),
            })
            if (sync.ok) {
              res = await pubFetch('/api/auth/session')
            }
          }
        }

        if (cancelled) return

        if (res.ok) {
          const data = await res.json()
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user))
          }
          setReady(true)
          return
        }

        localStorage.removeItem('token')
        localStorage.removeItem('user')
        router.replace('/login')
      } catch {
        if (!cancelled) setReady(true)
      }
    }

    setReady(false)
    validate()
    return () => {
      cancelled = true
    }
  }, [pathname, router])

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return <>{children}</>
}
